import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';
import { fetchInvoiceDetails } from '../../../services/SalesService';
import SitesOrderService, { SITES_ORDER_DISPLAY_OFFSET } from '../../../services/SitesOrderService';
import { getUnitById } from '../../../utils/Enums';
import { formatMoney } from '../../../utils/currency';

/** Normaliza contenido indefinido: API puede devolver undefinedContent (camelCase) o undefined_content (snake_case). */
const getUndefinedContentList = (item) => {
  const list = item?.undefinedContent ?? item?.undefined_content;
  if (!Array.isArray(list)) return [];
  return list;
};

/** Normaliza opcionales elegidos: API puede devolver optionalSelections (camelCase) o optional_selections (snake_case). */
const getOptionalSelectionsList = (item) => {
  const list = item?.optionalSelections ?? item?.optional_selections;
  if (!Array.isArray(list)) return [];
  return list;
};

const TOLERANCE = 0.02;

/**
 * Expande ítems con descuento tipo 3x2 (lleva X paga Y) en líneas separadas para la vista,
 * igual que en el PDF del ticket: N líneas a precio normal + M líneas con Dto = precio (Total 0).
 * Solo afecta la visualización.
 */
function expandBuyXPayYItemsForDisplay(items) {
  if (!Array.isArray(items)) return [];
  return items.flatMap((item) => {
    const quantity = item.quantity ?? 0;
    const unitPrice = item.unitPrice ?? 0;
    const rawDiscount = item.discountAmount ?? 0;
    const isShipping = (item.productName ?? '') === 'Envío';
    if (isShipping || unitPrice <= 0 || rawDiscount <= 0) return [item];

    const totalAsStored = rawDiscount;
    const totalAsPerUnit = rawDiscount * quantity;
    const candidates = [totalAsStored, totalAsPerUnit].filter((t) => t > 0);

    for (const totalDiscount of candidates) {
      const freeD = totalDiscount / unitPrice;
      const free = Math.round(freeD);
      if (Math.abs(freeD - free) < TOLERANCE && free >= 1 && free < quantity) {
        const paidUnits = quantity - free;
        const paidRows = Array.from({ length: paidUnits }, () => ({
          ...item,
          quantity: 1,
          unitPrice,
          discountAmount: 0,
          subtotal: unitPrice,
          undefinedContent: [],
          undefined_content: [],
        }));
        const freeRows = Array.from({ length: free }, () => ({
          ...item,
          quantity: 1,
          unitPrice,
          discountAmount: unitPrice,
          subtotal: 0,
          undefinedContent: [],
          undefined_content: [],
        }));
        return [...paidRows, ...freeRows];
      }
    }
    return [item];
  });
}

/** Fila de producto: Cantidad, P.U (unitPrice), D.U (discountAmount), Total. API puede enviar discountAmount y subtotal por unidad o por línea. */
const ProductRow = ({ item, t }) => {
  const [open, setOpen] = useState(false);
  const quantity = item.quantity ?? 0;
  const unitPrice = item.unitPrice ?? 0;
  const rawDiscount = item.discountAmount ?? 0;
  const rawSubtotal = item.subtotal ?? 0;
  const discountPerUnit = quantity > 0 && rawDiscount > unitPrice ? rawDiscount / quantity : rawDiscount;
  const lineTotalFromSubtotal = quantity > 0 && rawSubtotal > unitPrice ? rawSubtotal : quantity * rawSubtotal;
  const lineTotal = rawSubtotal > 0 ? lineTotalFromSubtotal : quantity * (unitPrice - discountPerUnit);
  const undefinedContentList = getUndefinedContentList(item);
  const optionalSelectionsList = getOptionalSelectionsList(item);
  const hasUndefined = undefinedContentList.length > 0;
  const hasOptionals = optionalSelectionsList.length > 0;
  const hasExpandable = hasUndefined || hasOptionals;

  return (
    <>
      <TableRow>
        <TableCell align="center" sx={{ width: 48, height: 40 }}>
          {hasExpandable && (
            <IconButton size="small" onClick={() => setOpen(!open)} aria-label={open ? t('collapse') || 'Contraer' : t('expand') || 'Expandir'}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {item.productName ?? t('product') ?? 'Producto'}
          </Typography>
        </TableCell>
        <TableCell align="center">{quantity}</TableCell>
        <TableCell align="right">{formatMoney(unitPrice)}</TableCell>
        <TableCell align="right">
          {discountPerUnit > 0 ? (
            <Typography component="span" color="success.main">-{formatMoney(discountPerUnit)}</Typography>
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell align="right">{formatMoney(lineTotal)}</TableCell>
      </TableRow>
      {hasExpandable && (
        <TableRow>
          <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 6, pt: 1, pb: 2 }}>
                {hasUndefined && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('products_used') || 'Productos utilizados'}:
                    </Typography>
                    <Table size="small" sx={{ width: '95%', mb: hasOptionals ? 2 : 0 }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell align="center">{t('name') || 'Nombre'}</TableCell>
                          <TableCell align="center">{t('quantity') || 'Cantidad'}</TableCell>
                          <TableCell align="center">{t('unit') || 'Unidad'}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {undefinedContentList.map((content, i) => (
                          <TableRow key={i}>
                            <TableCell align="center">{content.productName ?? content.product_name ?? '—'}</TableCell>
                            <TableCell align="center">{content.quantity ?? 0}</TableCell>
                            <TableCell align="center">{getUnitById(content.unit ?? content.unit_id)?.label ?? 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
                {hasOptionals && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('optionals_selected') || 'Opcionales elegidos'}:
                    </Typography>
                    <Table size="small" sx={{ width: '95%' }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell align="center">{t('name') || 'Nombre'}</TableCell>
                          <TableCell align="right">{t('extra_price') || 'Precio extra'}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {optionalSelectionsList.map((opt, i) => (
                          <TableRow key={i}>
                            <TableCell align="center">{opt.name ?? opt.productName ?? '—'}</TableCell>
                            <TableCell align="right">
                              {opt.extraPrice != null && Number(opt.extraPrice) > 0
                                ? `+${formatMoney(Number(opt.extraPrice))}`
                                : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const OrderDetailModal = ({ open, onClose, order }) => {
  const { t } = useTranslation();
  const tr = (key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };
  const [invoice, setInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);

  useEffect(() => {
    if (!open || !order?.id) {
      setOrderDetail(null);
      setInvoice(null);
      return;
    }
    SitesOrderService.getOrderById(order.id)
      .then(setOrderDetail)
      .catch(() => setOrderDetail(null));
  }, [open, order?.id]);

  useEffect(() => {
    if (open && order?.invoiceId) {
      setInvoiceLoading(true);
      fetchInvoiceDetails(order.invoiceId)
        .then(setInvoice)
        .catch(() => setInvoice(null))
        .finally(() => setInvoiceLoading(false));
    } else {
      setInvoice(null);
    }
  }, [open, order?.invoiceId]);

  if (!order) return null;

  const displayOrder = orderDetail ?? order;
  const baseItems = displayOrder.items ?? order.items ?? [];
  const isDelivery = (displayOrder.deliveryType ?? order.deliveryType) === 'DELIVERY';
  const isPickup = (displayOrder.deliveryType ?? order.deliveryType) === 'PICKUP';
  const deliveryAddress = String(displayOrder.deliveryAddress ?? order.deliveryAddress ?? '').trim();
  const shippingNotes = String(displayOrder.shippingNotes ?? order.shippingNotes ?? '').trim();
  const locationName = String(displayOrder.locationName ?? order.locationName ?? '').trim();
  const shippingCost = Number(displayOrder.shippingCost ?? order.shippingCost ?? 0);
  const invoiceShippingItem = Array.isArray(invoice?.items)
    ? invoice.items.find((item) => {
        const name = String(item?.productName ?? '').toLowerCase();
        return item?.productId === 0 || name.includes('envio') || name.includes('envío') || name.includes('shipping');
      })
    : null;
  const fallbackShippingBase = Number(
    displayOrder.shippingBaseCost
      ?? displayOrder.shippingBase
      ?? order.shippingBaseCost
      ?? order.shippingBase
      ?? shippingCost
  );
  const invoiceShippingUnitPrice = Number(invoiceShippingItem?.unitPrice ?? 0);
  const invoiceShippingDiscount = Number(invoiceShippingItem?.discountAmount ?? 0);
  const shippingUnitPriceForDisplay = (invoiceShippingUnitPrice > shippingCost && invoiceShippingDiscount > 0)
    ? invoiceShippingUnitPrice
    : fallbackShippingBase;
  const shippingDiscountForDisplay = (invoiceShippingDiscount > 0)
    ? invoiceShippingDiscount
    : Math.max(0, shippingUnitPriceForDisplay - shippingCost);
  const itemsForTable = (isDelivery && shippingCost > 0)
    ? [
        ...baseItems,
        {
          productId: 0,
          productName: tr('shipping', 'Envío'),
          quantity: 1,
          unitPrice: shippingUnitPriceForDisplay,
          discountAmount: shippingDiscountForDisplay,
          subtotal: Number(invoiceShippingItem?.subtotal ?? shippingCost),
          totalPrice: shippingCost,
          undefinedContent: [],
          optionalSelections: []
        }
      ]
    : baseItems;

  const getShippingStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'DELIVERED': return 'success';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'COMPLETED': return 'success';
      case 'CANCELED': return 'error';
      default: return 'default';
    }
  };

  const getShippingStatusLabel = (status) => {
    const labels = {
      'PENDING': t('pending') || 'Pendiente',
      'DELIVERED': t('delivered') || 'Entregado',
    };
    return labels[status] || status;
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      'PENDING': t('pending') || 'Pendiente',
      'COMPLETED': t('completed') || 'Finalizado',
      'CANCELED': t('canceled') || 'Anulada',
      'UNPAID': t('pending') || 'Pendiente',
      'PARTIAL': t('pending') || 'Pendiente',
      'PAID': t('completed') || 'Finalizado',
      'REFUNDED': t('pending') || 'Pendiente'
    };
    return labels[status] || status;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {t('order_details') || 'Detalles del Pedido'} - #{order.id != null ? SITES_ORDER_DISPLAY_OFFSET + order.id : '-'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {/* Dos columnas: Cliente (izq) y Orden (der) */}
          <Grid container spacing={3} sx={{ mb: 2 }}>
            {/* Columna izquierda: Información del Cliente */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                {t('customer_information') || 'Información del Cliente'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {t('name') || 'Nombre'}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {order.firstName} {order.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {t('email') || 'Email'}
                  </Typography>
                  <Typography variant="body1">
                    {order.email}
                  </Typography>
                </Grid>
                {order.phone && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {t('phone') || 'Teléfono'}
                    </Typography>
                    <Typography variant="body1">
                      {order.phone}
                    </Typography>
                  </Grid>
                )}
                {order.clientId && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {t('client_id') || 'CI'}
                    </Typography>
                    <Typography variant="body1">
                      {order.clientId}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* Columna derecha: Información de la Orden */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                {t('order_information') || 'Información de la Orden'}
              </Typography>
              <Grid container spacing={2}>
                {/* Fila 1: ID Factura | Fecha */}
                {order.invoiceId && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('invoice_id') || 'ID de Factura'}
                    </Typography>
                    <Typography variant="body1">
                      #{order.invoiceId}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={order.invoiceId ? 6 : 12}>
                  <Typography variant="body2" color="text.secondary">
                    {t('order_date') || 'Fecha'}
                  </Typography>
                  <Typography variant="body1">
                    {dayjs(order.orderDate).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                </Grid>
                {/* Fila 2: Método de Pago | Estado de Pago */}
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('payment_method') || 'Método de Pago'}
                  </Typography>
                  <Typography variant="body1">
                    {order.paymentMethod === 'CASH' ? (t('cash') || 'Efectivo') :
                     order.paymentMethod === 'TRANSFER' ? (t('transfer') || 'Transferencia') :
                     order.paymentMethod === 'POS' ? 'POS' :
                     order.paymentMethod === 'MERCADOPAGO' ? (t('mercadopago') || 'Mercado Pago') :
                     order.paymentMethod}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('payment_status') || 'Estado de Pago'}
                  </Typography>
                  <Chip
                    label={getPaymentStatusLabel(order.paymentStatus)}
                    color={getPaymentStatusColor(order.paymentStatus)}
                    size="small"
                    sx={{ mt: 0.5, height: 20, minHeight: 20, py: 0, fontSize: '0.7rem', lineHeight: 1, '& .MuiChip-label': { px: 0.75, py: 0 } }}
                  />
                </Grid>
                {/* Fila 3: Tipo de Entrega | Estado de entrega */}
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('delivery_type') || 'Tipo de Entrega'}
                  </Typography>
                  <Typography variant="body1">
                    {order.deliveryType === 'PICKUP'
                      ? (t('pickup_at_branch') || 'Retiro en sucursal')
                      : (t('delivery') || 'Envío')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('shipping_status') || 'Estado de entrega'}
                  </Typography>
                  <Chip
                    label={getShippingStatusLabel(order.shippingStatus)}
                    color={order.shippingStatus === 'PICKED_UP' ? 'default' : getShippingStatusColor(order.shippingStatus)}
                    size="small"
                    sx={{
                      mt: 0.5, height: 20, minHeight: 20, py: 0, fontSize: '0.7rem', lineHeight: 1, '& .MuiChip-label': { px: 0.75, py: 0 },
                      ...(order.shippingStatus === 'PICKED_UP' && { bgcolor: '#fdd835', color: 'rgba(0,0,0,0.87)' })
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Información de entrega manual */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            <Divider sx={{ my: 2 }} />
            {tr('delivery_information', 'Información de entrega')}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                {t('delivery_type') || 'Tipo de Entrega'}
              </Typography>
              <Typography variant="body1">
                {isPickup
                  ? (t('pickup_at_branch') || 'Retiro en sucursal')
                  : (t('delivery') || 'Envío')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                {t('shipping_status') || 'Estado de entrega'}
              </Typography>
              <Chip
                label={getShippingStatusLabel(displayOrder.shippingStatus || 'PENDING')}
                color={getShippingStatusColor(displayOrder.shippingStatus || 'PENDING')}
                size="small"
                sx={{
                  mt: 0.5, height: 20, minHeight: 20, py: 0, fontSize: '0.7rem', lineHeight: 1, '& .MuiChip-label': { px: 0.75, py: 0 },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                {isPickup ? (t('branch') || 'Sucursal') : (t('delivery_address') || 'Dirección de Envío')}
              </Typography>
              <Typography variant="body1">
                {isPickup
                  ? (locationName || '-')
                  : (deliveryAddress || '-')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                {t('shipping_cost') || 'Costo de envío'}
              </Typography>
              <Typography variant="body1">
                {formatMoney(shippingCost)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {t('shipping_notes') || 'Notas de envío'}
              </Typography>
              <Typography variant="body1">
                {shippingNotes || '-'}
              </Typography>
            </Grid>
          </Grid>


          {/* Facturación con RUT */}
          {order.invoiceWithRut && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                {t('billing_information') || 'Información de Facturación'}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {order.rut && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('rut') || 'RUT'}
                    </Typography>
                    <Typography variant="body1">
                      {order.rut}
                    </Typography>
                  </Grid>
                )}
                {order.razonSocial && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('razon_social') || 'Razón Social'}
                    </Typography>
                    <Typography variant="body1">
                      {order.razonSocial}
                    </Typography>
                  </Grid>
                )}
                {order.fiscalAddress && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {t('fiscal_address') || 'Dirección Fiscal'}
                    </Typography>
                    <Typography variant="body1">
                      {order.fiscalAddress}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </>
          )}

          {/* Notas de la compra (solo si tiene) */}
          {order.notes?.trim() && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                {t('purchase_notes') || 'Notas de la compra'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {order.notes}
              </Typography>
            </>
          )}

          {/* Items del Pedido */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>
            {t('order_items') || 'Items del Pedido'}
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                  <TableCell align="center" sx={{ width: 48 }} />
                  <TableCell>{t('product') || 'Producto'}</TableCell>
                  <TableCell align="center">{t('quantity') || 'Cantidad'}</TableCell>
                  <TableCell align="right">{t('unit_price') || 'Precio Unitario'}</TableCell>
                  <TableCell align="right">{t('discount_unit') || 'D. unitario'}</TableCell>
                  <TableCell align="right">{t('total') || 'Total'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itemsForTable.map((item, index) => (
                  <ProductRow
                    key={index}
                    item={{
                      ...item,
                      productName: item.productName ?? `Producto ID: ${item.productId}`,
                      subtotal: item.totalPrice ?? item.subtotal ?? 0,
                    }}
                    t={t}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totales */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Box sx={{ minWidth: 300 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('subtotal') || 'Subtotal'}:
                </Typography>
                <Typography variant="body2">
                  {formatMoney(Number(displayOrder.subtotal ?? order.subtotal ?? 0))}
                </Typography>
              </Box>
              {Number(displayOrder.discountAmount ?? order.discountAmount ?? 0) > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('discount') || 'Descuento'}:
                  </Typography>
                  <Typography variant="body2" color="success">
                    -{formatMoney(Number(displayOrder.discountAmount ?? order.discountAmount ?? 0))}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">
                  {t('total') || 'Total'}:
                </Typography>
                <Typography variant="h6" color="primary">
                  {formatMoney(Number(displayOrder.total ?? order.total ?? 0))}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t('close') || 'Cerrar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailModal;
